import React, { useContext, useState, useEffect } from "react";
import Ctx from "../../contexts/AppContext";

/**
 * CampaignDisplay — renders the Super-Admin broadcast for the current
 * user (festival greeting popup + banner, and the landlord discount
 * banner). Driven entirely by `appSettings.campaign`, which the server
 * has ALREADY filtered by audience/role in /sync, so whatever arrives
 * here is meant for this user.
 *
 *   appSettings.campaign = {
 *     greeting: { enabled, id, title, message, imageUrl, color, audience } | null,
 *     discount: { enabled, text } | null,
 *   }
 *
 * Behavior:
 *   - Greeting popup shows ONCE per campaign id (remembered in
 *     localStorage), then a slim dismissible banner remains for the
 *     campaign's duration.
 *   - Discount banner (landlords) taps through to the Upgrade screen.
 */
export default function CampaignDisplay() {
  const { h, appSettings, role, go, setOpenUpgradeOnSettings } = useContext(Ctx);
  const campaign = appSettings?.campaign || null;
  const greeting = campaign?.greeting || null;
  const discount = campaign?.discount || null;

  const greetId = greeting?.id || "";

  // Popup visibility: open once per campaign id (unless already
  // dismissed this campaign on this device).
  const [popupOpen, setPopupOpen] = useState(false);
  // Banner visibility: shown after popup is closed; user can also X it.
  const [bannerClosed, setBannerClosed] = useState(false);
  const [discountClosed, setDiscountClosed] = useState(false);

  useEffect(() => {
    if (!greeting || !greetId) { setPopupOpen(false); return; }
    let dismissed = "";
    try { dismissed = localStorage.getItem("campaignPopupSeen") || ""; } catch (_) {}
    setPopupOpen(dismissed !== greetId);
  }, [greeting, greetId]);

  // Reset per-campaign banner dismissals when the campaign id changes.
  useEffect(() => {
    setBannerClosed(false);
  }, [greetId]);

  const closePopup = () => {
    setPopupOpen(false);
    try { localStorage.setItem("campaignPopupSeen", greetId); } catch (_) {}
  };

  const openUpgrade = () => {
    // Reuse the existing deep-link: land on Settings and auto-open the
    // Upgrade modal.
    if (typeof setOpenUpgradeOnSettings === "function") setOpenUpgradeOnSettings(true);
    go("settings");
  };

  // Open the admin's external banner link in a new tab/browser.
  // noopener/noreferrer for tab-nabbing safety. No-op if blank.
  const openLink = (url) => {
    if (!url) return;
    try { window.open(url, "_blank", "noopener,noreferrer"); } catch (_) {}
  };

  const color = greeting?.color || "#7c3aed";

  return (
    <>
      {/* ── FESTIVAL GREETING POPUP (once per campaign) ── */}
      {greeting && popupOpen && (
        <div
          onClick={closePopup}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="animate-scaleIn"
            style={{
              width: "100%", maxWidth: 360, background: h.cd, borderRadius: 22,
              overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              border: `1px solid ${color}40`,
            }}
          >
            {/* Decorative header band */}
            <div style={{ background: `linear-gradient(160deg, ${color} 0%, ${color}cc 100%)`, padding: greeting.imageUrl ? 0 : "26px 20px 22px", textAlign: "center", position: "relative" }}>
              {greeting.imageUrl ? (
                <img
                  src={greeting.imageUrl} alt=""
                  onClick={greeting.linkUrl ? () => openLink(greeting.linkUrl) : undefined}
                  title={greeting.linkUrl ? "Open offer" : undefined}
                  style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block", cursor: greeting.linkUrl ? "pointer" : "default" }}
                />
              ) : (
                <div style={{ fontSize: 44 }}>🎉</div>
              )}
            </div>
            <div style={{ padding: "20px 22px 24px", textAlign: "center" }}>
              <div style={{ color: color, fontWeight: 800, fontSize: 22, marginBottom: 8, lineHeight: 1.2 }}>{greeting.title || "🎉"}</div>
              {greeting.message && <div style={{ color: h.tx, fontSize: 14, lineHeight: 1.55 }}>{greeting.message}</div>}
              <button
                onClick={closePopup}
                style={{ marginTop: 20, width: "100%", padding: 13, borderRadius: 12, border: "none", background: color, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}
              >
                {"Thank you 🙏"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FESTIVAL BANNER (persists for the campaign) ──
          When an image is set it leads the banner (full-width image
          card); tapping it opens the admin's external link (browser,
          noopener) if provided. Without an image it falls back to the
          compact text strip. A small ↗ badge signals an external link. */}
      {greeting && !popupOpen && !bannerClosed && (
        greeting.imageUrl ? (
          <div style={{
            position: "relative", borderRadius: 16, overflow: "hidden",
            border: `1px solid ${color}33`, margin: "12px 0 4px",
            boxShadow: `0 2px 10px ${color}22`,
          }}>
            <div
              onClick={() => openLink(greeting.linkUrl)}
              style={{ cursor: greeting.linkUrl ? "pointer" : "default" }}
              title={greeting.linkUrl ? "Open offer" : undefined}
            >
              <img src={greeting.imageUrl} alt={greeting.title || "Festival"} style={{ width: "100%", maxHeight: 180, objectFit: "cover", display: "block" }} />
              {(greeting.title || greeting.message) && (
                <div style={{ background: `linear-gradient(135deg, ${color}1F 0%, ${color}0A 100%)`, padding: "10px 12px" }}>
                  {greeting.title && <div style={{ color: color, fontWeight: 700, fontSize: 13 }}>{greeting.title}</div>}
                  {greeting.message && <div style={{ color: h.ts || h.sc, fontSize: 11, marginTop: 2 }}>{greeting.message}</div>}
                </div>
              )}
              {greeting.linkUrl && (
                <span style={{ position: "absolute", top: 8, left: 8, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 999, backdropFilter: "blur(4px)" }}>
                  {"Tap to view ↗"}
                </span>
              )}
            </div>
            <button onClick={() => setBannerClosed(true)} aria-label="Dismiss"
              style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.55)", color: "#fff", border: "none", borderRadius: 999, width: 26, height: 26, cursor: "pointer", fontSize: 16, lineHeight: 1, backdropFilter: "blur(4px)" }}>×</button>
          </div>
        ) : (
          <div
            onClick={() => greeting.linkUrl && openLink(greeting.linkUrl)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              background: `linear-gradient(135deg, ${color}1F 0%, ${color}0A 100%)`,
              border: `1px solid ${color}33`, borderRadius: 14,
              padding: "10px 12px", margin: "12px 0 4px",
              cursor: greeting.linkUrl ? "pointer" : "default",
            }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>🎉</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: color, fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{greeting.title}</div>
              {greeting.message && <div style={{ color: h.ts || h.sc, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{greeting.message}</div>}
            </div>
            {greeting.linkUrl && <span style={{ color: color, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{"↗"}</span>}
            <button onClick={(e) => { e.stopPropagation(); setBannerClosed(true); }} aria-label="Dismiss" style={{ background: "none", border: "none", color: h.ts || h.sc, cursor: "pointer", fontSize: 18, lineHeight: 1, flexShrink: 0, padding: 4 }}>×</button>
          </div>
        )
      )}

      {/* ── LANDLORD DISCOUNT BANNER ── */}
      {role === "landlord" && discount && discount.text && !discountClosed && (
        <div
          onClick={openUpgrade}
          style={{
            display: "flex", alignItems: "center", gap: 10,
            background: `${h.ac}12`, border: `1px solid ${h.ac}33`, borderRadius: 14,
            padding: "11px 12px", margin: "12px 0 4px", cursor: "pointer",
          }}
        >
          <span style={{ fontSize: 18, flexShrink: 0 }}>🏷️</span>
          <span style={{ flex: 1, minWidth: 0, color: h.ac, fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{discount.text}</span>
          <span style={{ color: h.ac, fontSize: 16, fontWeight: 700, flexShrink: 0 }}>{"›"}</span>
          <button onClick={(e) => { e.stopPropagation(); setDiscountClosed(true); }} aria-label="Dismiss" style={{ background: "none", border: "none", color: h.ts || h.sc, cursor: "pointer", fontSize: 18, lineHeight: 1, flexShrink: 0, padding: "0 2px" }}>×</button>
        </div>
      )}
    </>
  );
}
